import {ThemeControl} from '../theme';
import { useEffect, useState } from "react";
import api from "../axiosConfig";
import AdminUserUsagePanel from "./AdminUserUsagePanel";

const CARRIER_GATEWAYS = {
  att: "txt.att.net",
  cricket: "sms.cricketwireless.net",
  spectrum_mobile: "vtext.com",
  t_mobile: "tmomail.net",
  mint: "tmomail.net",
  verizon: "vtext.com",
};

const CARRIER_OPTIONS = [
  { value: "", label: "Select carrier" },
  { value: "spectrum_mobile", label: "Spectrum Mobile" },
  { value: "verizon", label: "Verizon" },
  { value: "att", label: "AT&T" },
  { value: "t_mobile", label: "T-Mobile" },
  { value: "mint", label: "Mint" },
  { value: "cricket", label: "Cricket" },
];

function normalizePhoneNumber(value) {
  return String(value || "").replace(/\D/g, "").slice(-10);
}

function buildTextReminderAddress(phoneNumber, carrier) {
  const digits = normalizePhoneNumber(phoneNumber);
  const domain = CARRIER_GATEWAYS[carrier] || "";
  if (!digits || digits.length !== 10 || !domain) return "";
  return `${digits}@${domain}`;
}

function buildInitialForm(user) {
  const existingPreferences = user?.preferences && typeof user.preferences === "object"
    ? user.preferences
    : {};
  const textReminderCarrier = existingPreferences.textReminderCarrier || "";
  const textReminderAddress = existingPreferences.textReminderAddress
    || buildTextReminderAddress(existingPreferences.phoneNumber, textReminderCarrier);

  return {
    name: user?.name || "",
    email: user?.email || "",
    nickname: user?.nickname || "",
    preferences: {
      ...existingPreferences,
      compactLayout: Boolean(existingPreferences.compactLayout),
      emailNotifications: Boolean(existingPreferences.emailNotifications),
      phoneNumber: existingPreferences.phoneNumber || "",
      textReminderCarrier,
      textReminderAddress,
    },
  };
}

export default function ProfileSettingsForm({ user, onUserUpdated, onBack }) {
  const [form, setForm] = useState(() => buildInitialForm(user));
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    setForm(buildInitialForm(user));
    setIsEditing(false);
  }, [user]);

  const resetForm = () => {
    setForm(buildInitialForm(user));
    setIsEditing(false);
    setMessage("");
    setError("");
  };

  const syncReminderDestination = (preferences, overrides = {}) => {
    const nextPreferences = {
      ...preferences,
      ...overrides,
    };
    return {
      ...nextPreferences,
      textReminderAddress: buildTextReminderAddress(
        nextPreferences.phoneNumber,
        nextPreferences.textReminderCarrier,
      ),
    };
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage("");
    setError("");

    try {
      const res = await api.put("/api/profile", {
        name: form.name,
        email: form.email,
        nickname: form.nickname,
        preferences: form.preferences,
      });
      onUserUpdated?.(res.data.user);
      setMessage("Profile updated.");
      setIsEditing(false);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to update profile.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="profile-panel">
      <span className="eyebrow">Account</span>
      <div className="section-heading">
        <h2>Profile Settings</h2>
        <div className="editor-actions">
          {!isEditing ? (
            <button
              type="button"
              className="action-button"
              onClick={() => {
                setIsEditing(true);
                setMessage("");
                setError("");
              }}
            >
              Edit profile
            </button>
          ) : (
            <button type="button" className="action-button-secondary" onClick={resetForm}>
              Cancel
            </button>
          )}
          <button type="button" className="action-button-secondary" onClick={onBack}>
            Back to dashboard
          </button>
        </div>
      </div>
      <ThemeControl label="Appearance on this browser" />
      <form className="profile-settings-form" onSubmit={handleSubmit}>
        <label className="editor-field">
          <span>Name</span>
          <input
            name="name"
            value={form.name}
            disabled={!isEditing || saving}
            onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
            placeholder="Your display name"
          />
        </label>

        <label className="editor-field">
          <span>Email</span>
          <input
            type="email"
            name="email"
            value={form.email}
            disabled={!isEditing || saving}
            onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
            placeholder="your@email.com"
          />
        </label>

        <label className="editor-field">
          <span>Nickname</span>
          <input
            name="nickname"
            value={form.nickname}
            disabled={!isEditing || saving}
            onChange={(event) => setForm((current) => ({ ...current, nickname: event.target.value }))}
            placeholder="Optional short label"
          />
        </label>

        <label className="editor-field">
          <span>Phone number</span>
          <input
            type="tel"
            name="phoneNumber"
            value={form.preferences.phoneNumber}
            disabled={!isEditing || saving}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                preferences: {
                    ...current.preferences,
                    ...syncReminderDestination(current.preferences, {
                      phoneNumber: event.target.value,
                    }),
                  },
                }))
            }
            placeholder="Optional contact number"
          />
        </label>

        <label className="editor-field">
          <span>Mobile carrier</span>
          <select
            name="textReminderCarrier"
            value={form.preferences.textReminderCarrier}
            disabled={!isEditing || saving}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                preferences: {
                  ...syncReminderDestination(current.preferences, {
                    textReminderCarrier: event.target.value,
                  }),
                },
              }))
            }
          >
            {CARRIER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <small>Used to generate calendar reminder texts from your phone number.</small>
        </label>

        <label className="editor-field">
          <span>Text reminder address</span>
          <input
            type="text"
            name="textReminderAddress"
            value={form.preferences.textReminderAddress}
            disabled
            readOnly
            placeholder="Generated from phone number and carrier"
          />
          <small>{form.preferences.textReminderAddress || "Enter a 10-digit phone number and select a carrier."}</small>
        </label>

        <div className="profile-preferences">
          <span className="stat-label">Preferences</span>
          <label className="profile-checkbox">
            <input
              type="checkbox"
              checked={form.preferences.compactLayout}
              disabled={!isEditing || saving}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  preferences: {
                    ...current.preferences,
                    compactLayout: event.target.checked,
                  },
                }))
              }
            />
            <span>Prefer compact layout density</span>
          </label>
          <label className="profile-checkbox">
            <input
              type="checkbox"
              checked={form.preferences.emailNotifications}
              disabled={!isEditing || saving}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  preferences: {
                    ...current.preferences,
                    emailNotifications: event.target.checked,
                  },
                }))
              }
            />
            <span>Enable account email notifications</span>
          </label>
        </div>

        <div className="profile-summary">
          <div>
            <dt>Email</dt>
            <dd>{form.email || "Not available"}</dd>
          </div>
          <div>
            <dt>Phone</dt>
            <dd>{form.preferences.phoneNumber || "Not available"}</dd>
          </div>
          <div>
            <dt>Text reminders</dt>
            <dd>{form.preferences.textReminderAddress || "Not available"}</dd>
          </div>
          <div>
            <dt>Access</dt>
            <dd>{user?.isAdmin ? "Admin" : "Standard user"}</dd>
          </div>
        </div>

        {error ? <div className="feedback-panel error">{error}</div> : null}
        {message ? <div className="feedback-panel">{message}</div> : null}

        {isEditing ? (
          <div className="editor-actions">
            <button className="action-button" type="submit" disabled={saving}>
              {saving ? "Saving..." : "Save settings"}
            </button>
          </div>
        ) : null}
      </form>

      {user?.isAdmin ? <AdminUserUsagePanel /> : null}
    </section>
  );
}
